/**
 * Aba Etapas em edição (PR4). Edita inline nome/objetivo/critério de cada etapa,
 * adiciona/remove e reordena (setas ↑/↓ — acessível, sem dependência de DnD).
 *
 * Ações (`actions`) são heterogêneas: array de strings (NetComet) OU de objetos
 * `{ type, template|keywords|target }` (Triagem). Para NÃO corromper o shape de
 * objeto, ações de objeto são exibidas em leitura (formatStepAction) e não viram
 * editáveis; ações de string (ou etapa nova) são editadas como lista de linhas.
 */

import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';

import { formatStepAction } from '../playbook-labels';
import { makeStep } from '../use-playbook-form';

import { StepGovernanceEditor } from './StepGovernanceEditor';

import type { PlaybookStep } from '../types';
import type { PlaybookForm } from '../use-playbook-form';

/** Uma etapa tem ações editáveis se todas as ações são strings (ou não há ações). */
function actionsAreEditable(actions: unknown[] | undefined): boolean {
  if (actions === undefined || actions.length === 0) return true;
  return actions.every((a) => typeof a === 'string');
}

export function TabStepsEdit({ form }: { form: PlaybookForm }): JSX.Element {
  const { draft, set } = form;
  const steps = draft.steps;

  const update = (index: number, patch: Partial<PlaybookStep>): void => {
    set(
      'steps',
      steps.map((s, i) => (i === index ? { ...s, ...patch } : s)),
    );
  };

  // Substitui a etapa inteira (os setters de governança removem chaves quando o
  // valor volta ao default — `update` por-patch não consegue deletar chaves).
  const replace = (index: number, next: PlaybookStep): void => {
    set(
      'steps',
      steps.map((s, i) => (i === index ? next : s)),
    );
  };

  const add = (): void => {
    set('steps', [...steps, makeStep(crypto.randomUUID())]);
  };

  const remove = (index: number): void => {
    set(
      'steps',
      steps.filter((_, i) => i !== index),
    );
  };

  const move = (index: number, dir: -1 | 1): void => {
    const target = index + dir;
    if (target < 0 || target >= steps.length) return;
    const next = [...steps];
    const [moved] = next.splice(index, 1);
    next.splice(target, 0, moved!);
    set('steps', next);
  };

  return (
    <div className="pb-panel" role="tabpanel" aria-label="Etapas">
      <div className="pb-panel__head-row">
        <h2 className="pb-panel__title">Etapas</h2>
        <button type="button" className="pb-btn pb-btn--sm" onClick={add}>
          <Plus size={14} /> Adicionar etapa
        </button>
      </div>

      {steps.length > 0 ? (
        <div className="pb-step-list">
          {steps.map((step, index) => {
            const stringActions = (step.actions ?? []).filter(
              (a): a is string => typeof a === 'string',
            );
            const editableActions = actionsAreEditable(step.actions);
            return (
              <div key={step.id} className="pb-step-card pb-step-card--edit">
                <div className="pb-step-card__edit-head">
                  <span className="pb-step-card__num">#{index + 1}</span>
                  <div className="pb-step-card__edit-actions">
                    <button
                      type="button"
                      className="pb-btn pb-btn--ghost pb-btn--sm"
                      aria-label={`Mover etapa ${index + 1} para cima`}
                      disabled={index === 0}
                      onClick={() => {
                        move(index, -1);
                      }}
                    >
                      <ArrowUp size={14} />
                    </button>
                    <button
                      type="button"
                      className="pb-btn pb-btn--ghost pb-btn--sm"
                      aria-label={`Mover etapa ${index + 1} para baixo`}
                      disabled={index === steps.length - 1}
                      onClick={() => {
                        move(index, 1);
                      }}
                    >
                      <ArrowDown size={14} />
                    </button>
                    <button
                      type="button"
                      className="pb-btn pb-btn--ghost pb-btn--sm"
                      aria-label={`Remover etapa ${index + 1}`}
                      onClick={() => {
                        remove(index);
                      }}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="pb-field">
                  <span className="pb-field__label">Nome da etapa</span>
                  <input
                    type="text"
                    className="pb-input"
                    value={step.name}
                    aria-label={`Nome da etapa ${index + 1}`}
                    onChange={(e) => {
                      update(index, { name: e.target.value });
                    }}
                  />
                </div>

                <div className="pb-field">
                  <span className="pb-field__label">Objetivo</span>
                  <textarea
                    className="pb-textarea"
                    rows={2}
                    value={step.objective ?? ''}
                    aria-label={`Objetivo da etapa ${index + 1}`}
                    onChange={(e) => {
                      update(index, { objective: e.target.value });
                    }}
                  />
                </div>

                <div className="pb-field">
                  <span className="pb-field__label">Ações</span>
                  {editableActions ? (
                    <div className="pb-action-editor">
                      {stringActions.map((action, ai) => (
                        <div key={ai} className="pb-action-row">
                          <input
                            type="text"
                            className="pb-input"
                            value={action}
                            aria-label={`Ação ${ai + 1} da etapa ${index + 1}`}
                            onChange={(e) => {
                              const next = [...stringActions];
                              next[ai] = e.target.value;
                              update(index, { actions: next });
                            }}
                          />
                          <button
                            type="button"
                            className="pb-btn pb-btn--ghost pb-btn--sm"
                            aria-label={`Remover ação ${ai + 1} da etapa ${index + 1}`}
                            onClick={() => {
                              update(index, {
                                actions: stringActions.filter((_, i) => i !== ai),
                              });
                            }}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      ))}
                      <button
                        type="button"
                        className="pb-btn pb-btn--sm"
                        onClick={() => {
                          update(index, { actions: [...stringActions, ''] });
                        }}
                      >
                        <Plus size={14} /> Adicionar ação
                      </button>
                    </div>
                  ) : (
                    <>
                      <ul className="pb-step-card__actions">
                        {(step.actions ?? []).map((action, ai) => (
                          <li key={ai}>{formatStepAction(action)}</li>
                        ))}
                      </ul>
                      <span className="pb-read-value pb-read-value--muted">
                        Ações estruturadas (não editáveis nesta tela).
                      </span>
                    </>
                  )}
                </div>

                <div className="pb-field">
                  <span className="pb-field__label">Critério de sucesso</span>
                  <input
                    type="text"
                    className="pb-input"
                    value={step.successCriteria ?? ''}
                    aria-label={`Critério de sucesso da etapa ${index + 1}`}
                    onChange={(e) => {
                      update(index, { successCriteria: e.target.value });
                    }}
                  />
                </div>

                <StepGovernanceEditor
                  step={step}
                  indexLabel={String(index + 1)}
                  onChange={(next) => {
                    replace(index, next);
                  }}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <p className="pb-read-value pb-read-value--muted">
          Nenhuma etapa. Use “Adicionar etapa” para criar a primeira.
        </p>
      )}
    </div>
  );
}
